import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { formatDateAsYyyyMmDdLocal } from '@app/core/utils/format-local-iso-date';
import {
  buildRequestCopyPlainText,
  IPatientHistory,
  IPatientRequestDetail,
  IPatientRequestTest,
  LAB_TYPE,
  PatientRequestsService,
  RequestCopyLabels,
} from '@app/core/patient-requests';
import { UsersService } from '@app/core/users';
import { ToastrService } from 'ngx-toastr';
import { defaultHeaderConfig } from './default-header-config';
import { FileService } from '@app/core/files';

type ReferenceDateMode = 'concrete' | 'request';

@Component({
  selector: 'afoone-patient-request-detail',
  templateUrl: './patient-request-detail.component.html',
  styleUrls: ['./patient-request-detail.component.css'],
})
export class PatientRequestDetailComponent implements OnChanges {
  @Input('request') public record: IPatientRequestDetail;
  @Input('patient') public patient: IPatientHistory;
  /** Si true, no se muestra el panel Acumulados (p. ej. pantalla 1.4 abierta desde tabla de acumulados) [[19]]. */
  @Input() public hideAcumuladosPanel = false;
  /** Si true, no se muestra el panel Epidemiología / sensibilidad ATB en pantalla 1.4 abierta desde epidemiología (#671). */
  @Input() public hideEpidemiologyPanel = false;
  public headerItems: ItemConfig[] = defaultHeaderConfig;
  /** Rango permitido para "Anteriores a" [[9]]: 01/01/1900 – fecha actual. */
  minAcumuladosDate = new Date(1900, 0, 1);
  /**
   * Debe ser una referencia estable (no getter con `new Date()` en cada CD): si `[maxDate]` cambia
   * en cada detección de cambios, PrimeNG Calendar reinicia el panel y no permite pasar de mes.
   */
  maxAcumuladosDate = new Date();
  public testsColumns: ItemConfig[] = [];

  public exportingFlag = false;

  // Acumulados panel state
  public referenceDateMode: ReferenceDateMode = 'concrete';
  public referenceDateConcrete: Date = new Date();
  public requisTypes: { codRequisType: number; requisType: string }[] = [];
  public selectedRequisType: number = 0;
  public selectedTestsForAcumulados: IPatientRequestTest[] = [];
  public acumuladosLoading = false;
  public acumuladosResult: unknown[] | null = null;
  public acumuladosError: string | null = null;

  /** Diálogo copiar petición como texto (#140). */
  public copyDialogVisible = false;
  public copyDialogText = '';

  get showPrintButton() {
    return this.record?.materiales?.length
      ? true
      : this.record.tests?.filter((t) => t.metname !== 'Peticion sin técnicas')
          .length > 0;
  }

  get canShowGraficasEvolucion(): boolean {
    const n = this.selectedTestsForAcumulados?.length ?? 0;
    return n >= 1 && n <= 10;
  }

  /** Panel epidemiología / ATB (Func. 3): solo LABM. */
  get isMicrobiologyLab(): boolean {
    return this.record?.labType === LAB_TYPE.LABM;
  }

  get canShowEpidemiologyPanel(): boolean {
    return !this.hideEpidemiologyPanel && this.isMicrobiologyLab;
  }

  /** Código 3 letras para enlaces `view-request` desde vista epidemiología (p. ej. MIC). */
  get microbiologyLaboratoryCode(): string {
    const raw = (this.record?.laboratorio ?? this.record?.labName ?? 'MIC').toString().trim();
    return raw.slice(0, 3).toUpperCase() || 'MIC';
  }

  get defaultSisifoCenterId(): number | null {
    return this.userService.getSisifoDefaultCenter();
  }

  /** Panel Acumulados solo se muestra si el tipo de laboratorio es LABC (análisis clínicos) [[6]][[8]]. */
  get isLabTypeLABC(): boolean {
    const t = this.record?.labType;
    if (t === 'LABC') return true;
    if (t === 'LABM' || t === 'LABG') return false;
    const name = ((this.record as any)?.labName ?? (this.record as any)?.statName ?? (this.record as any)?.statname ?? '').toUpperCase();
    if (/MICRO|LABM/.test(name)) return false;
    if (/GENETIC|GEN\b|LABG/.test(name)) return false;
    return true;
  }

  constructor(
    private service: PatientRequestsService,
    private fileService: FileService,
    private router: Router,
    private userService: UsersService,
    private translate: TranslateService,
    private _toastr: ToastrService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['record'] && this.record) {
      this.maxAcumuladosDate = new Date();
      this.loadRequisTypes();
      this.selectedTestsForAcumulados = [];
      this.acumuladosResult = null;
      this.acumuladosError = null;
    }
  }

  private isHeader(test: IPatientRequestTest): boolean {
    return !test.mikrotyp && [10, 12, 13].includes(test.restyp);
  }

  private isComment(test: IPatientRequestTest, index: number, tests: IPatientRequestTest[]): boolean {
    if (index === 0) return false;
    return test.metname === tests[index - 1].metname;
  }

  /** WIR-RF-03.01.3.1: no dejar la fecha “Anteriores a” vacía. */
  onAcumuladosReferenceDateChange(value: Date | null): void {
    if (value == null || Number.isNaN(value.getTime())) {
      this.referenceDateConcrete = new Date();
    }
  }

  private loadRequisTypes(): void {
    this.service.getRequisTypes().subscribe({
      next: (list) => {
        this.requisTypes = list ?? [];
        if (this.requisTypes.length && this.selectedRequisType === undefined) {
          this.selectedRequisType = this.requisTypes[0].codRequisType;
        }
      },
      error: () => (this.requisTypes = []),
    });
  }

  /** Fecha de petición para wspi_Search_Cumu: DD/MM/YYYY HH:mm, sin parseo ambiguo MM/DD. */
  private formatRequestDateForAcumulados(raw: unknown): string | null {
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
      return this.formatDdMmYyyyHhMm(raw);
    }
    if (typeof raw !== 'string' && typeof raw !== 'number') {
      return null;
    }
    return this.formatRequestDateStringForAcumulados(String(raw).trim());
  }

  private formatRequestDateStringForAcumulados(value: string): string | null {
    if (!value) {
      return null;
    }

    const eu = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[T\s]+(\d{1,2}):(\d{2}))?/.exec(value);
    if (eu) {
      return this.formatDatePartsForAcumulados(
        Number(eu[3]),
        Number(eu[2]),
        Number(eu[1]),
        eu[4] == null ? 0 : Number(eu[4]),
        eu[5] == null ? 0 : Number(eu[5])
      );
    }

    const iso = /^(\d{4})[-.](\d{1,2})[-.](\d{1,2})(?:[T\s]+(\d{1,2}):(\d{2}))?/.exec(value);
    if (iso) {
      return this.formatDatePartsForAcumulados(
        Number(iso[1]),
        Number(iso[2]),
        Number(iso[3]),
        iso[4] == null ? 0 : Number(iso[4]),
        iso[5] == null ? 0 : Number(iso[5])
      );
    }

    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime())
      ? null
      : this.formatDdMmYyyyHhMm(fallback);
  }

  private formatDatePartsForAcumulados(
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number
  ): string | null {
    const d = new Date(year, month - 1, day, hour, minute, 0, 0);
    if (
      Number.isNaN(d.getTime()) ||
      d.getFullYear() !== year ||
      d.getMonth() !== month - 1 ||
      d.getDate() !== day
    ) {
      return null;
    }
    return this.formatDdMmYyyyHhMm(d);
  }

  private formatDdMmYyyyHhMm(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  /** Reference date for API: fecha concreta ISO o fecha de petición DD/MM/YYYY HH:mm (#490). */
  getReferenceDateForApi(): string {
    if (this.referenceDateMode === 'request') {
      const requestDate =
        this.formatRequestDateForAcumulados(this.record?.einDat) ??
        this.formatRequestDateForAcumulados(this.record?.cEinDat);
      if (requestDate) {
        return requestDate;
      }
    }
    const d = this.referenceDateConcrete ?? new Date();
    return d instanceof Date ? formatDateAsYyyyMmDdLocal(d) : formatDateAsYyyyMmDdLocal(new Date());
  }

  buscarAcumulados(): void {
    if (!this.record || !this.patient) return;
    this.acumuladosError = null;
    this.acumuladosLoading = true;
    const testList = this.selectedTestsForAcumulados?.length
      ? this.selectedTestsForAcumulados.map((t) => t.metodo).join(',')
      : undefined;
    const params = {
      patientId: this.patient.historia,
      labNr: this.record.labNr,
      referenceDate: this.getReferenceDateForApi(),
      requisType: this.selectedRequisType,
      testList: testList || '',
      originRequisNr: String(this.record.peticion ?? this.record.auftrNr ?? ''),
      originLaboratorio: this.record.laboratorio ?? (this.record.labName?.slice(0, 3) ?? ''),
      originPeticion: String(this.record.peticion ?? ''),
      originLabor: this.record.labor ?? this.record.labNr ?? 0,
      originDate: this.record.einDat ?? '',
      originImpreso: this.record.impreso ?? '.',
      originSitio: this.record.sitio ?? 'D',
    };
    this.service
      .searchAcumulados({
        patientId: params.patientId,
        labNr: params.labNr,
        referenceDate: params.referenceDate,
        requisType: params.requisType,
        testList: params.testList || undefined,
        graphicFormat: 0,
      })
      .subscribe({
        next: (res) => {
          this.acumuladosLoading = false;
          const data = Array.isArray(res) ? res : res?.data ?? [];
          const msgCode = (res as { msgCode?: string })?.msgCode;
          const msgType = (res as { msgType?: number })?.msgType;
          this.acumuladosResult = data;
          if (data.length) {
            this.openAcumuladosTab(params);
          } else if (msgCode && msgType !== 0) {
            this.acumuladosError = this.translate.instant('ACUMULADOS.' + msgCode);
          }
        },
        error: (err) => {
          this.acumuladosLoading = false;
          this.acumuladosResult = null;
          this.acumuladosError = err?.message || err?.error?.message || 'Error al buscar acumulados';
        },
      });
  }

  /** Abre la pantalla Tabla de acumulados (1.2) en nueva pestaña. Incluye params de la petición origen para enlace "Ver resultados petición". */
  openAcumuladosTab(params: {
    patientId: string; labNr: number; referenceDate: string; requisType: number; testList: string; originRequisNr: string;
    originLaboratorio?: string; originPeticion?: string; originLabor?: number; originDate?: string; originImpreso?: string; originSitio?: string;
  }): void {
    const centroId = this.userService.getSisifoDefaultCenter();
    const q: Record<string, string | number | undefined> = {
      patientId: params.patientId,
      labNr: params.labNr,
      referenceDate: params.referenceDate,
      requisType: params.requisType,
      testList: params.testList || undefined,
      originRequisNr: params.originRequisNr || undefined,
    };
    if (centroId != null) q['centroId'] = centroId;
    if (params.originLaboratorio) q['originLaboratorio'] = params.originLaboratorio;
    if (params.originPeticion) q['originPeticion'] = params.originPeticion;
    if (params.originLabor != null) q['originLabor'] = params.originLabor;
    if (params.originDate) q['originDate'] = params.originDate;
    if (params.originImpreso != null) q['originImpreso'] = params.originImpreso;
    if (params.originSitio != null) q['originSitio'] = params.originSitio;
    const tree = this.router.createUrlTree(['/patient-requests', 'acumulados-view'], {
      queryParams: q,
      queryParamsHandling: '',
    });
    const url = this.router.serializeUrl(tree);
    const full = globalThis.location.origin + (url.startsWith('/') ? url : '/' + url);
    globalThis.open(full, '_blank');
  }

  graficasEvolucion(): void {
    const n = this.selectedTestsForAcumulados?.length ?? 0;
    if (n < 1 || n > 10) {
      this.acumuladosError = this.translate.instant('ACUMULADOS.CUMULATIVE_SELECT_TESTS');
      return;
    }
    if (!this.record || !this.patient) return;
    this.acumuladosError = null;
    this.acumuladosLoading = true;
    const testList = this.selectedTestsForAcumulados.map((t) => t.metodo).join(',');
    const referenceDate = this.getReferenceDateForApi();
    this.service
      .searchAcumulados({
        patientId: this.patient.historia,
        labNr: this.record.labNr,
        referenceDate,
        requisType: this.selectedRequisType,
        testList,
        graphicFormat: 1,
      })
      .subscribe({
        next: (res) => {
          this.acumuladosLoading = false;
          const data = Array.isArray(res) ? res : res?.data ?? [];
          const msgCode = (res as { msgCode?: string })?.msgCode;
          const msgType = (res as { msgType?: number })?.msgType;
          if (!data.length && msgCode && msgType !== 0) {
            this.acumuladosError = this.translate.instant('ACUMULADOS.' + msgCode);
            return;
          }
          this.openEvolutionChartsTab({
            patientId: this.patient.historia,
            labNr: this.record.labNr,
            referenceDate,
            requisType: this.selectedRequisType,
            testList,
            originRequisNr: String(this.record.peticion ?? this.record.auftrNr ?? ''),
          });
        },
        error: (err) => {
          this.acumuladosLoading = false;
          this.acumuladosResult = null;
          this.acumuladosError = err?.message || err?.error?.message || 'Error al buscar acumulados';
        },
      });
  }

  /** Abre la pantalla Gráficas de evolución en nueva pestaña. */
  openEvolutionChartsTab(params: {
    patientId: string;
    labNr: number;
    referenceDate: string;
    requisType: number;
    testList: string;
    originRequisNr?: string;
  }): void {
    const centroId = this.userService.getSisifoDefaultCenter();
    const q: Record<string, string | number | undefined> = {
      patientId: params.patientId,
      labNr: params.labNr,
      referenceDate: params.referenceDate,
      requisType: params.requisType,
      testList: params.testList || undefined,
      originRequisNr: params.originRequisNr || undefined,
    };
    if (centroId != null) q['centroId'] = centroId;
    const tree = this.router.createUrlTree(['/patient-requests', 'evolution-charts'], {
      queryParams: q,
      queryParamsHandling: '',
    });
    const url = this.router.serializeUrl(tree);
    const full = globalThis.location.origin + (url.startsWith('/') ? url : '/' + url);
    globalThis.open(full, '_blank');
  }

  async exportAsPDF() {
    if (this.exportingFlag) {
      return;
    }
    this.exportingFlag = true;
    const response = await firstValueFrom(
      this.service.generateRequestPDF(
        this.record.auftrNr,
        this.record.einDat + ':00',
        this.record.labNr,
        this.patient.historia
      )
    );
    const { filename } = response;
    this.fileService.getFile(filename);

    this.exportingFlag = false;
  }

  private buildCopyLabels(): RequestCopyLabels {
    const t = (key: string) => this.translate.instant(key);
    return {
      disclaimer1: t('REQUEST_COPY.DISCLAIMER_1'),
      disclaimer2: t('REQUEST_COPY.DISCLAIMER_2'),
      disclaimer3: t('REQUEST_COPY.DISCLAIMER_3'),
      colTechnique: t('REQUEST_DATA.TEST.TECNICA'),
      colResults: t('REQUEST_DATA.TEST.RESULTADOS'),
      colUnits: t('REQUEST_DATA.TEST.UNIDADES'),
      colRanges: t('REQUEST_DATA.TEST.RANGOS'),
      colAntibiotic: t('REQUEST_DATA.TEST.ANTIBIOTICO'),
      colInterpretation: t('REQUEST_COPY.COL_INTERP'),
      labelRequest: t('REQUEST_COPY.LABEL_REQUEST'),
      labelDate: t('REQUEST_COPY.LABEL_DATE'),
      labelNhc: t('REQUEST_COPY.LABEL_NHC'),
    };
  }

  openCopyDialog(): void {
    if (!this.record) return;
    this.copyDialogText = buildRequestCopyPlainText(
      this.record,
      this.patient ?? null,
      this.buildCopyLabels()
    );
    this.copyDialogVisible = true;
  }

  closeCopyDialog(): void {
    this.copyDialogVisible = false;
  }

  async copyPlainTextToClipboard(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.copyDialogText);
      this._toastr.success(
        this.translate.instant('REQUEST_COPY.COPY_OK'),
        '',
        { timeOut: 2500 }
      );
    } catch {
      this._toastr.error(
        this.translate.instant('REQUEST_COPY.COPY_ERROR'),
        '',
        { timeOut: 4000 }
      );
    }
  }
}

export type ItemConfig = {
  key: string;
  span: number;
  pipe?: (value: any) => string;
};
